import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, Clock, Shield, Users, Star } from 'lucide-react';

const DailyPickDrop = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: Clock, title: 'Punctual Service', description: 'Always on time, every time' },
    { icon: Shield, title: 'Safe & Secure', description: 'Verified drivers and GPS tracking' },
    { icon: Users, title: 'Professional Drivers', description: 'Experienced and courteous staff' },
    { icon: Star, title: 'Affordable Rates', description: 'Best prices with no hidden charges' },
  ];

  const handleBookNow = () => {
    const message = 'Hi, I would like to inquire about Daily Pick & Drop Service from Advait Tour and Travels.';
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <>
      <SEO
        title="Daily Pick & Drop Service - Office Commute | Advait Tour and Travels"
        description="Reliable daily pick and drop service for office, school, and regular commutes in Chandigarh, Mohali, and Panchkula. Affordable monthly packages with professional drivers."
        keywords="daily pick drop service, office cab service, daily commute taxi, monthly cab service chandigarh, office pickup drop"
        canonicalUrl={`${window.location?.origin}/daily-pick-drop`}
        ogTitle="Daily Pick & Drop Service - Office Commute | Advait Tour and Travels"
        ogDescription="Reliable daily pick and drop service for office, school, and regular commutes. Affordable monthly packages with professional drivers."
        ogUrl={`${window.location?.origin}/daily-pick-drop`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Daily Pick & Drop Service",
          "description": "Daily commute service for office and regular travel",
          "provider": {
            "@type": "Organization",
            "name": "Advait Tour and Travels"
          }
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Daily Pick & Drop Service
              </h1>
              <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
                Your Reliable Partner for Daily Commute - Office, School, and More
              </p>
              <button
                onClick={handleBookNow}
                className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold text-lg rounded-lg transition-all duration-200"
              >
                Book Now
              </button>
            </div>
          </div>

          {/* Features Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features?.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature?.title}</h3>
                  <p className="text-gray-600">{feature?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Our Daily Pick & Drop Service?</h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  Tired of the daily commute hassle? Let <strong>Advait Tour and Travels</strong> take care of your transportation needs with our reliable Daily Pick & Drop Service. Whether you need a ride to the office, school, or any regular destination, we ensure you reach on time, every time.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Our Service Highlights</h3>
                
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Punctuality Guaranteed:</strong> We value your time. Our drivers arrive on schedule to ensure you never miss an important meeting or appointment.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Flexible Packages:</strong> Choose from daily, weekly, or monthly packages tailored to your needs and budget.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Professional Drivers:</strong> All our drivers are verified, experienced, and trained to provide courteous service.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Safe & Comfortable:</strong> Travel in well-maintained, clean vehicles equipped with GPS tracking for your safety.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Affordable Rates:</strong> Transparent pricing with no hidden charges. Get the best value for your money.</span>
                  </li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Who Can Benefit?</h3>
                
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><strong>Office Professionals:</strong> Skip the stress of driving in traffic and arrive fresh at work.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><strong>Students:</strong> Safe and reliable transportation for school or college.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><strong>Senior Citizens:</strong> Comfortable rides for medical appointments or daily errands.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><strong>Corporate Teams:</strong> Group transportation solutions for your employees.</span>
                  </li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How It Works</h3>
                
                <ol className="space-y-3 ml-6 list-decimal">
                  <li><strong>Book Your Service:</strong> Contact us via phone or website to schedule your pick and drop timings.</li>
                  <li><strong>Choose Your Package:</strong> Select from our flexible daily, weekly, or monthly plans.</li>
                  <li><strong>Enjoy Hassle-Free Commute:</strong> Sit back and relax while our professional driver takes care of the rest.</li>
                </ol>

                <div className="bg-blue-50 rounded-lg p-6 mt-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-3">Service Areas</h3>
                  <p className="text-gray-700">
                    We provide daily pick and drop services across Chandigarh, Mohali, Panchkula, Zirakpur, Kharar, and surrounding areas. Contact us for service availability in your location.
                  </p>
                </div>

                <p className="mt-8">
                  Make your daily commute stress-free with <strong>Advait Tour and Travels</strong>. Book your Daily Pick & Drop Service today and experience the convenience of reliable, professional transportation.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DailyPickDrop;